import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

const springGentle = { type: 'spring', stiffness: 300, damping: 35 };

export default function Auth() {
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'register' ? 'register' : 'login';

  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');

  // Register form
  const [regEmail, setRegEmail] = useState('');
  const [regPw, setRegPw] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regFullName, setRegFullName] = useState('');

  useEffect(() => {
    if (user) navigate('/search', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(loginEmail, loginPw);
    setLoading(false);
    if (error) {
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } else {
      toast.success('Welcome back!');
      navigate('/search');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regUsername.trim()) { toast.error('Username is required'); return; }
    if (regPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    const { error } = await signUp(regEmail, regPw, regUsername.toLowerCase().replace(/\s+/g, '_'), regFullName);
    setLoading(false);
    if (error) {
      toast.error(error.message || 'Registration failed.');
    } else {
      toast.success('Account created! Please check your email to confirm, then log in.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 75%, black) 100%)',
        }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 80%, white 1px, transparent 1px), radial-gradient(circle at 70% 20%, white 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-xl text-white">Pivot-Mate</span>
        </Link>
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
            Your goals deserve a witness.
          </h2>
          <p className="text-white/75 text-lg leading-relaxed max-w-sm">
            Connect with motivated partners, set shared goals, and finally build the consistency you've been chasing.
          </p>
          <div className="mt-10 flex flex-col gap-4">
            {['Real-time partner chat', 'Shared task calendar', 'Smart partner matching'].map(item => (
              <div key={item} className="flex items-center gap-3 text-white/80 text-sm">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs">✓</span>
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
          <p className="text-white/40 text-xs relative z-10">© 2026 Pivot-Mate</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 self-start">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm text-muted-foreground">Back to home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springGentle}
          className="w-full max-w-md"
        >
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 rounded-xl h-11">
              <TabsTrigger value="login" className="rounded-lg font-medium">Sign In</TabsTrigger>
              <TabsTrigger value="register" className="rounded-lg font-medium">Create Account</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-foreground">Welcome back</h1>
                <p className="text-muted-foreground text-sm mt-1">Sign in to your Pivot-Mate account</p>
              </div>
              <form onSubmit={handleLogin} className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="login-email">Email address</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    required
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="login-pw">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-pw"
                      type={showPw ? 'text' : 'password'}
                      placeholder="Your password"
                      value={loginPw}
                      onChange={e => setLoginPw(e.target.value)}
                      required
                      className="h-11 rounded-xl pr-10"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(!showPw)}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="h-11 rounded-xl font-semibold mt-1" disabled={loading}
                  style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-foreground">Create your account</h1>
                <p className="text-muted-foreground text-sm mt-1">Join Pivot-Mate and find your partner today</p>
              </div>
              <form onSubmit={handleRegister} className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="reg-fullname">Full Name</Label>
                    <Input id="reg-fullname" placeholder="Jane Doe" value={regFullName} onChange={e => setRegFullName(e.target.value)} required className="h-11 rounded-xl" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="reg-username">Username</Label>
                    <Input id="reg-username" placeholder="jane_doe" value={regUsername} onChange={e => setRegUsername(e.target.value)} required className="h-11 rounded-xl" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="reg-email">Email address</Label>
                  <Input id="reg-email" type="email" placeholder="you@example.com" value={regEmail} onChange={e => setRegEmail(e.target.value)} required className="h-11 rounded-xl" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="reg-pw">Password <span className="text-muted-foreground text-xs">(min 8 chars)</span></Label>
                  <div className="relative">
                    <Input id="reg-pw" type={showPw ? 'text' : 'password'} placeholder="Choose a strong password" value={regPw} onChange={e => setRegPw(e.target.value)} required className="h-11 rounded-xl pr-10" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(!showPw)}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="h-11 rounded-xl font-semibold mt-1" disabled={loading}
                  style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}>
                  {loading ? 'Creating account…' : 'Create Account'}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  By signing up, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
